/**
 * Connect to the pending bridge endpoint, open the browser for sign-in,
 * and call onAuth when the backend sends workspaceId + token.
 */

import WebSocket from 'ws';
import { CLI_VERSION } from '../cli-version.js';
import { createWsBridge, sendWsMessage } from '../connection/create-ws-bridge.js';
import { BRIDGE_HEARTBEAT_SEQ_MAX } from '../connection/heartbeat/constants.js';
import type { BridgeToApiHeartbeatMessage } from '../types/bridge-to-api-messages.js';
import { openBrowser } from './open-browser.js';
import { log, type LogFn } from '../log.js';
import { logCliWebSocketError, safeCloseWebSocket } from '../connection/cli-ws-client.js';
import {
  beginDeferredDisconnectForReconnect,
  clearReconnectQuietOnSuccessfulConnection,
  clearReconnectQuietTimer,
  createEmptyReconnectQuietSlot,
  formatReconnectDelayForLog,
  pendingAuthReconnectDelayMs,
} from '../connection/reconnect/index.js';

const PENDING_KEEPALIVE_MS = 25000;
const BROWSER_OPEN_FALLBACK_MS = 4000;
function buildPendingBridgeUrl(apiUrl: string, connectionId: string): string {
  const base = apiUrl.startsWith('https')
    ? apiUrl.replace(/^https/, 'wss')
    : apiUrl.replace(/^http/, 'ws');
  const params = new URLSearchParams({ connectionId });
  return `${base}/ws/bridge/pending?${params.toString()}`;
}

export interface AuthResult {
  workspaceId: string;
  token: string;
  refreshToken?: string;
}

export interface RunPendingAuthOptions {
  apiUrl: string;
  initialWorkspaceId?: string;
  preferredBridgeName?: string;
  onAuth: (auth: AuthResult) => void;
  log?: LogFn;
}

export interface PendingAuthHandle {
  close: () => void;
  authPromise: Promise<AuthResult | null>;
}

export function runPendingAuth(options: RunPendingAuthOptions): PendingAuthHandle {
  const { apiUrl, initialWorkspaceId, preferredBridgeName, onAuth } = options;
  const logFn = options.log ?? log;

  let ws: WebSocket | null = null;
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  let keepaliveInterval: ReturnType<typeof setInterval> | null = null;
  let browserFallback: ReturnType<typeof setTimeout> | null = null;
  const connectionId = crypto.randomUUID();
  let hasOpenedBrowser = false;
  let resolved = false;
  let resolveAuth: (value: AuthResult | null) => void;
  const authPromise = new Promise<AuthResult | null>((resolve) => {
    resolveAuth = resolve;
  });

  let reconnectAttempt = 0;
  const signInQuiet = createEmptyReconnectQuietSlot();

  function clearQuietOnOpen(): void {
    clearReconnectQuietOnSuccessfulConnection(
      signInQuiet,
      logFn,
      '[Bridge service] Sign-in connection restored.',
    );
    reconnectAttempt = 0;
  }

  function beginDeferredPendingCloseLog(code: number, reason: string): void {
    beginDeferredDisconnectForReconnect({
      isClosedByUser: () => resolved,
      quiet: signInQuiet,
      code,
      reason,
      willReconnect: true,
      log: logFn,
      serviceLabel: '[Bridge service]',
      shutdownDetail: 'Not reconnecting (shutting down).',
      reconnectingDetail: 'Waiting for browser sign-in; reconnecting…',
      shouldAbortQuietWindow: () => ws != null && ws.readyState === WebSocket.OPEN,
    });
  }

  function cleanup(): void {
    clearReconnectQuietTimer(signInQuiet);
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
    if (browserFallback) {
      clearTimeout(browserFallback);
      browserFallback = null;
    }
    if (keepaliveInterval) {
      clearInterval(keepaliveInterval);
      keepaliveInterval = null;
    }
    if (ws) {
      const w = ws;
      ws = null;
      safeCloseWebSocket(w);
    }
  }

  function connect(): void {
    const url = buildPendingBridgeUrl(apiUrl, connectionId);
    let pendingHbSeq = -1;
    ws = createWsBridge({
      url,
      onOpen: () => {
        clearQuietOnOpen();
        pendingHbSeq = -1;
        sendWsMessage(ws!, { type: 'identify', role: 'cli', cliVersion: CLI_VERSION });
        keepaliveInterval = setInterval(() => {
          if (resolved || !ws || ws.readyState !== 1) return;
          pendingHbSeq = pendingHbSeq >= BRIDGE_HEARTBEAT_SEQ_MAX ? 0 : pendingHbSeq + 1;
          const hb: BridgeToApiHeartbeatMessage = { t: 'h', s: pendingHbSeq };
          sendWsMessage(ws, hb);
        }, PENDING_KEEPALIVE_MS);
        if (browserFallback) {
          clearTimeout(browserFallback);
          browserFallback = null;
        }
        if (!hasOpenedBrowser) {
          hasOpenedBrowser = true;
          void openBrowser(connectionId, initialWorkspaceId, preferredBridgeName, apiUrl, logFn);
        }
      },
      onClose: (code, reason) => {
        if (keepaliveInterval) {
          clearInterval(keepaliveInterval);
          keepaliveInterval = null;
        }
        if (resolved) return;
        beginDeferredPendingCloseLog(code, reason);
        const delay = pendingAuthReconnectDelayMs(reconnectAttempt);
        reconnectAttempt += 1;
        if (signInQuiet.verboseLogs) {
          const delayLabel = formatReconnectDelayForLog(delay);
          logFn(
            `[Bridge service] Next sign-in connection attempt in ${delayLabel} (attempt ${reconnectAttempt}).`,
          );
        }
        reconnectTimeout = setTimeout(() => {
          reconnectTimeout = null;
          connect();
        }, delay);
      },
      onError: (err) => logCliWebSocketError(logFn, '[Bridge service]', err, 'while waiting for sign-in'),
      onMessage: (data) => {
        const msg = data as { type?: string; token?: string; workspaceId?: string; refreshToken?: string };
        if (msg.type === 'auth_token' && typeof msg.token === 'string') {
          resolved = true;
          const workspaceId = typeof msg.workspaceId === 'string' ? msg.workspaceId : '';
          cleanup();
          const refreshToken = typeof msg.refreshToken === 'string' ? msg.refreshToken : undefined;
          const result: AuthResult = { workspaceId, token: msg.token, refreshToken };
          resolveAuth(result);
          onAuth(result);
        }
      },
    });
  }

  browserFallback = setTimeout(() => {
    browserFallback = null;
    if (!hasOpenedBrowser) {
      hasOpenedBrowser = true;
      void openBrowser(connectionId, initialWorkspaceId, preferredBridgeName, apiUrl, logFn);
    }
  }, BROWSER_OPEN_FALLBACK_MS);
  connect();

  return {
    close: () => {
      if (!resolved) resolveAuth(null);
      cleanup();
    },
    authPromise,
  };
}
